import * as types from '@/store/mutationTypes';
import errorCaseSid from '@/lib/const/errorCaseSid';

function setInterceptorBatch(argHash) {
  /**
   * This function needs to be called after the store modularization process to ensure
   * it refers to the correct store module.
   */
  const { axiosInstance, store } = argHash;

  // http request interceptors
  axiosInstance.interceptors.request.use(
    async (config) => {
      const isCrossTenantSessionEnabled = store.getters['authorisation/crossTenantSessionEnabled'];
      if (isCrossTenantSessionEnabled) {
        config.headers.Authorization = '';
      }

      if (config.headers.Authorization && !config.headers.Authorization.startsWith('Bearer')) {
        const token = store.getters['authorisation/getToken'];
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // to handle auth0 case, where the token is set there
          delete config.headers.Authorization;
        }
      }

      if (
        store.state.common.impersonatedClientSid &&
        `${store.state.common.impersonatedClientSid}` !== '0'
      ) {
        // eslint-disable-next-line no-param-reassign
        config.headers['x-impersonated-client-sid'] = store.state.common.impersonatedClientSid;
      } else {
        // eslint-disable-next-line no-param-reassign
        config.headers['x-impersonated-client-sid'] = '';
      }
      return config;
    },
    (err) => {
      return Promise.reject(err);
    }
  );

  // http response interceptors
  axiosInstance.interceptors.response.use(
    (response) => {
      store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      return response;
    },
    async (error) => {
      // Gates only the dialog-driving commits below — the 401/403 branches always run
      // their auth/session handling regardless of this flag, since that must happen for
      // every failed request, not just ones that also show a dialog.
      const suppressErrorDialogCommit = Boolean(
        error.config && error.config.suppressErrorDialogCommit
      );

      if (error && error.response && `${error.response.status}` === '401') {
        const responseErrorSid = error?.response?.data?.errorSid;

        if (
          responseErrorSid === errorCaseSid.USER_AUTH_SCHEME_IDP_RBACL_MIXED_ACL_ROLE_FETCH_FAILED
        ) {
          if (!store.state.common.isRbaclNotConfigured) {
            store.commit(types.COMMON_SET_IS_RBACL_NOT_CONFIGURED, true);
          }
          return Promise.reject(error);
        }

        store.commit(types.AUTHORISATION_SET_IS_AUTHENTICATED, false);
        const isHubEnabled = store.getters['authorisation/hubEnabled'];
        const shouldSkipHubLogout =
          isHubEnabled && Boolean(error.config && error.config.skipAuthLogout);
        if (isHubEnabled && !shouldSkipHubLogout) {
          const { error: retError, message: errorMessage } = error.response?.data || {};
          if (retError === 'Unauthorized' && errorMessage?.includes('ValidationError')) {
            try {
              await store.dispatch('hub-session/handleInvalidTokenInfo');
            } catch (e) {
              // logout failed due to bad token — force redirect to login
              window.location.href = `/login?errToken=invalid`;
            }
          } else {
            await store.dispatch('hub-session/logout', {}, { root: true });
          }
        }
      } else if (error && error.response && `${error.response.status}` === '403') {
        store.commit(types.AUTHORISATION_SET_IS_AUTHENTICATED, false);
      } else if (
        error &&
        error.response &&
        (`${error.response.status}` === '400' || `${error.response.status}` === '409')
      ) {
        // eslint-disable-next-line no-param-reassign
        error.response.errorDetected = true;
        const { errorSid, sentinelEventSid } = error.response.data;
        if (!suppressErrorDialogCommit && (Boolean(errorSid) || Boolean(sentinelEventSid))) {
          store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, true);
          store.commit(types.COMMON_SET_API_ERROR_MESSAGE, { errorSid, sentinelEventSid });
        }
      } else if (
        error &&
        error.response &&
        `${error.response.status}` === '503' &&
        error.response.data &&
        error.response.data.errorCaseSid
      ) {
        // eslint-disable-next-line no-param-reassign
        error.response.errorCaseSidDetected = true;
        const { errorSid, sentinelEventSid } = error.response.data;
        if (!suppressErrorDialogCommit && (Boolean(errorSid) || Boolean(sentinelEventSid))) {
          store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, true);
          store.commit(types.COMMON_SET_API_ERROR_MESSAGE, { errorSid, sentinelEventSid });
        }
      } else if (error && error.response && error.response.data) {
        const { errorSid, sentinelEventSid, message } = error.response.data;
        if (!suppressErrorDialogCommit) {
          if (Boolean(errorSid) || Boolean(sentinelEventSid)) {
            store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, true);
            store.commit(types.COMMON_SET_API_ERROR_MESSAGE, {
              errorSid,
              sentinelEventSid,
              message
            });
          } else {
            store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, true);
          }
        }
      } else if (!suppressErrorDialogCommit) {
        store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, true);
      }

      return Promise.reject(error);
    }
  );
}

export default {
  setInterceptorBatch
};
